import React, { lazy, Suspense } from "react";
import ReactDOM from "react-dom/client";
import { createBrowserRouter, RouterProvider } from "react-router-dom";
import "../global.css";
import { setupCSSPreloadErrorHandler } from "./components/ErrorBoundary";
import { StandaloneErrorPage } from "./components/StandaloneErrorPage";
import { setupAssetErrorHandler } from "./utils/assetErrorHandler";

const Layout = lazy(() => import("./components/Layout"));
const AcceptOrganizationInvitePage = lazy(
  () => import("./pages/AcceptOrganizationInvitePage"),
);
const AdminAllConsultationsPage = lazy(
  () => import("./pages/AdminAllConsultationsPage"),
);
const AdminAllOrganizationsPage = lazy(
  () => import("./pages/AdminAllOrganizationsPage"),
);
const AdminHomePage = lazy(() => import("./pages/AdminHomePage"));
const AdminOrganizationPage = lazy(
  () => import("./pages/AdminOrganizationPage"),
);
const CallPage = lazy(() => import("./pages/CallPage"));
const CallsListPage = lazy(() => import("./pages/CallsListPage"));
const CompletePasswordResetPage = lazy(
  () => import("./pages/CompletePasswordResetPage"),
);
const ConsultationAnalyticsPage = lazy(
  () => import("./pages/ConsultationAnalyticsPage"),
);
const ConsultationPage = lazy(() => import("./pages/ConsultationPage"));
const ConsultationsListPage = lazy(
  () => import("./pages/ConsultationsListPage"),
);
const CreateOrganizationPage = lazy(
  () => import("./pages/CreateOrganizationPage"),
);
const EmptyOrgPage = lazy(() => import("./pages/EmptyOrgPage"));
const HomePage = lazy(() => import("./pages/HomePage"));
const InitConsultationLandingPage = lazy(
  () => import("./pages/InitConsultationLandingPage"),
);
const LoginPage = lazy(() => import("./pages/LoginPage"));
const LogoutPage = lazy(() => import("./pages/LogoutPage"));
const OrganizationDirectRoutePage = lazy(
  () => import("./pages/OrganizationDirectRoutePage"),
);
const OrganizationPage = lazy(() => import("./pages/OrganizationPage"));
const RecorderPage = lazy(() => import("./pages/RecorderPage"));
const ResetPasswordPage = lazy(() => import("./pages/ResetPasswordPage"));
const SelectOrgPage = lazy(() => import("./pages/SelectOrgPage"));
const SignupPage = lazy(() => import("./pages/SignupPage"));
const UserConsultationsPage = lazy(
  () => import("./pages/UserConsultationsPage"),
);

// Set up error handlers for production asset loading
setupCSSPreloadErrorHandler();
setupAssetErrorHandler();

const rootElement = document.getElementById("root")!;
const root = ReactDOM.createRoot(rootElement);

const router = createBrowserRouter([
  {
    path: "/",
    element: <Layout />,
    errorElement: <StandaloneErrorPage />,
    children: [
      { index: true, element: <HomePage /> },
      { path: "signup", element: <SignupPage /> },
      { path: "login", element: <LoginPage /> },
      { path: "logout", element: <LogoutPage /> },
      { path: "password-reset", element: <ResetPasswordPage /> },
      {
        path: "complete-password-reset",
        element: <CompletePasswordResetPage />,
      },
      {
        path: "accept-organization-invite",
        element: <AcceptOrganizationInvitePage />,
      },
      { path: "create-organization", element: <CreateOrganizationPage /> },
      { path: "consultations/init", element: <InitConsultationLandingPage /> },
      { path: "consultations/:cid", element: <ConsultationPage /> },
      { path: "calls/:callid", element: <CallPage /> },
      { path: "admin", element: <AdminHomePage /> },
      { path: "admin/consultations", element: <AdminAllConsultationsPage /> },
      { path: "admin/organizations", element: <AdminAllOrganizationsPage /> },
      {
        path: "admin/organizations/:orgid",
        element: <AdminOrganizationPage />,
      },
      { path: "o", element: <EmptyOrgPage /> },
      { path: "o/select", element: <SelectOrgPage /> },
      {
        path: "o/:orgid",
        children: [
          { path: "calls", element: <CallsListPage /> },
          { path: "consultations", element: <ConsultationsListPage /> },
          {
            path: "consultation-analytics",
            element: <ConsultationAnalyticsPage />,
          },
          { path: "settings", element: <OrganizationPage /> },
          { path: "recording/:cid/:fid/:type?", element: <RecorderPage /> },
          {
            path: "u/:userid/consultations",
            element: <UserConsultationsPage />,
          },
          { index: true, element: <OrganizationDirectRoutePage /> },
        ],
      },
    ],
  },
]);

root.render(
  <React.StrictMode>
    <Suspense fallback={null}>
      <RouterProvider router={router} />
    </Suspense>
  </React.StrictMode>,
);
